import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { View, Text, Link, Card } from '@olxbr/design-system'
import Icons from '@olxbr/olx-icons'

import { formatTimestamp } from '../../../util/date'
import PauseIcon from '../../atoms/PauseIcon'
import DownloadStatusWaitingIcon from '../../atoms/DownloadStatusWaitingIcon'
import DownloadStatusReadyIcon from '../../atoms/DownloadStatusReadyIcon'
import _HRule from '../../atoms/HRule'
import { fetchRequests as fetchTakeout } from '../../../api/takeout'

const CardWrapperSlide = styled(Card)`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 24px;
  padding-top: 32px;
  padding-bottom: 32px;
  padding-left: 32px;
  padding-right: 32px;
  transition: all ease 1s;
  max-height: ${props => (props.open ? 500 : 112)}px;
  scroll-behavior: smooth;
`

const ContentOptions = styled(View)`
  margin-top: 24px;
`

const HRule = styled(_HRule)`
  border-top-width: 1px;
`

const HRuleWrapper = styled(View)`
  margin-top: 24px;
  margin-bottom: 24px;
`

const TitleContainer = styled(View)`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 32px;
`

const TitleContent = styled(View)`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`

const OpenOptionsIconWrapper = styled(View)`
  width: 40px;
  height: 24px;
  color: #6e0ad6;
  padding-left: 16px;
  cursor: pointer;
`

const Option = styled(View)`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const OptionDescription = styled(View)`
  display: flex;
  flex-direction: column;
  margin-left: 40px;
`

const IconWrapper = styled(View)`
  width: 24px;
  height: 24px;
  color: #4a4a4a;
  margin-right: 16px;
`

const OptionNameWrapper = styled(View)`
  flex-grow: 1;
`

const LinkWrapper = styled(Link)`
  font-size: 12px;
  padding-left: 4px;
  color: #999999;
`

const ElementReference = styled.div``

const OptionName = ({ children }) => (
  <OptionNameWrapper>
    <Text size="body1" weight="semiBold">
      {children}
    </Text>
  </OptionNameWrapper>
)

const OptionItem = ({ name, icon, linkText, descriptions, onClick, linkEnabled = true }) => (
  <React.Fragment>
    <Option>
      <IconWrapper>{icon}</IconWrapper>
      <OptionName>{name}</OptionName>
      <View style={linkEnabled ? {} : { cursor: 'not-allowed' }}>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <Link
          type={linkEnabled ? 'primary' : 'secondary'}
          underline={false}
          fontWeight={600}
          onClick={onClick}
          style={linkEnabled ? {} : { pointerEvents: 'none', color: '#D2D2D2' }}
        >
          {linkText}
        </Link>
      </View>
    </Option>
    <OptionDescription>
      {descriptions.map((description, idx) => (
        <Text key={idx.toString()} size="caption" color="grayscale.darker">
          {description}
        </Text>
      ))}
    </OptionDescription>
  </React.Fragment>
)

OptionItem.propTypes = {
  name: PropTypes.string,
  icon: PropTypes.string,
  linkText: PropTypes.string,
  descriptions: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  onClick: PropTypes.func,
  linkEnabled: PropTypes.bool,
}

class AccountDeleteCard extends React.Component {
  constructor(props) {
    super(props)
    this.takeoutStatusRef = React.createRef()
    this.state = {
      open: false,
      takeoutRequests: [],
    }
  }

  componentDidMount() {
    this.fetchTakeoutRequests()
  }

  setFocus = () => {
    if (this.props.focus) {
      this.toggleOptions()
      this.takeoutStatusRef.current.scrollIntoView()
    }
  }

  fetchTakeoutRequests = () => {
    fetchTakeout()
      .then(data => this.setState({ takeoutRequests: data.requests }))
      .finally(() => this.setFocus())
  }

  toggleOptions = () => {
    this.setState(prevState => ({ open: !prevState.open }))
  }

  renderDataDownloadOption = () => {
    const { takeoutRequests } = this.state
    const requestInProgress = takeoutRequests.find(request =>
      ['IN_PROGRESS', 'COMPLETED'].includes(request.status)
    )
    const requestReady = takeoutRequests.find(request => request.status === 'TAKEOUT_READY')

    const { onAccountTakeoutLink, onMyAccountLink } = this.props

    let icon = <Icons.System.Download />
    let linkText = 'solicitar'
    let linkEnabled = true
    let descriptions = [
      'Solicite a cópia dos seus dados pessoais',
      <React.Fragment>
        Você pode ver os principais dados da sua conta em
        <LinkWrapper href="#" onClick={onMyAccountLink}>
          Meu cadastro
        </LinkWrapper>
      </React.Fragment>,
    ]

    if (requestInProgress) {
      icon = <DownloadStatusWaitingIcon width={32} height={32} />
      linkText = 'solicitado'
      linkEnabled = false
      descriptions = [
        <React.Fragment>
          Solicitado em{' '}
          <Text size="caption" color="grayscale.darker" weight="semiBold">
            {formatTimestamp(requestInProgress.requestedAt)}
          </Text>
        </React.Fragment>,
        <React.Fragment>Estamos preparando seu arquivo de dados</React.Fragment>,
      ]
    }

    if (requestReady) {
      icon = <DownloadStatusReadyIcon />
      linkText = ''
      descriptions = [
        <React.Fragment>
          Enviado em{' '}
          <Text size="caption" color="grayscale.darker" weight="semiBold">
            {formatTimestamp(requestReady.updatedAt)}
          </Text>
        </React.Fragment>,
        <React.Fragment>Cópia disponível durante 7 dias</React.Fragment>,
      ]
    }

    return (
      <OptionItem
        name="Baixar dados pessoais"
        icon={icon}
        onClick={requestInProgress ? null : onAccountTakeoutLink}
        linkText={linkText}
        linkEnabled={linkEnabled}
        descriptions={descriptions}
      />
    )
  }

  render() {
    const { open } = this.state
    const { onDeactivateAccount, onDeleteAccount } = this.props
    return (
      <CardWrapperSlide open={open}>
        <ElementReference ref={this.takeoutStatusRef} />
        <TitleContainer>
          <TitleContent>
            <Text size="title3" weight="bold">
              Gerencie seus dados
            </Text>
            <Text size="body2">
              Você pode desativar, excluir sua conta e baixar seus dados pessoais.
            </Text>
          </TitleContent>
          <OpenOptionsIconWrapper onClick={this.toggleOptions}>
            {open ? <Icons.System.ChevronUp /> : <Icons.System.ChevronDown />}
          </OpenOptionsIconWrapper>
        </TitleContainer>
        <ContentOptions>
          <React.Fragment>
            {this.renderDataDownloadOption()}
            <HRuleWrapper>
              <HRule />
            </HRuleWrapper>
          </React.Fragment>
          <OptionItem
            name="Desativar conta temporariamente"
            icon={<PauseIcon />}
            onClick={onDeactivateAccount}
            linkText="desativar"
            descriptions={[
              'Seu perfil será desabilitado e anúncios excluídos',
              'Você pode reativar sua conta quando quiser',
            ]}
          />
          <HRuleWrapper>
            <HRule />
          </HRuleWrapper>
          <OptionItem
            name="Excluir conta definitivamente"
            icon={<Icons.System.Trash />}
            onClick={onDeleteAccount}
            linkText="excluir"
            descriptions={[
              'Dados da sua conta, anúncios e perfil serão excluídos definitivamente',
              'Não será possível recuperar sua conta',
            ]}
          />
        </ContentOptions>
      </CardWrapperSlide>
    )
  }
}

AccountDeleteCard.propTypes = {
  onDeleteAccount: PropTypes.func,
  onDeactivateAccount: PropTypes.func,
  onAccountTakeoutLink: PropTypes.func,
  onMyAccountLink: PropTypes.func,
  focus: PropTypes.bool,
}

export default AccountDeleteCard
